import React from 'react';
import { SlidersHorizontal, Sparkles, X } from 'lucide-react';
import { ClothingBrowse } from '../../../hooks/useClothingBrowse';
import { cleanBrand } from '../../../lib/clothingBrowseApi';
import { SORT_OPTIONS, SortKey } from '../../../data/clothing/browseConfig';

const GREEN = '#78BE20';

interface Chip { label: string; onRemove: () => void }

const chipsFor = (b: ClothingBrowse): Chip[] => {
  const { filters, toggle, update } = b;
  const chips: Chip[] = [];
  filters.types.forEach((t) => chips.push({ label: t, onRemove: () => toggle('types', t) }));
  filters.genders.forEach((g) => chips.push({ label: g === 'Male' ? 'Men' : g === 'Female' ? 'Women' : g, onRemove: () => toggle('genders', g) }));
  filters.ages.forEach((a) => chips.push({ label: a, onRemove: () => toggle('ages', a) }));
  filters.colours.forEach((c) => chips.push({ label: c, onRemove: () => toggle('colours', c) }));
  filters.brands.forEach((br) => chips.push({ label: cleanBrand(br), onRemove: () => toggle('brands', br) }));
  filters.sizes.forEach((s) => chips.push({ label: `Size ${s}`, onRemove: () => toggle('sizes', s) }));
  if (filters.min !== undefined || filters.max !== undefined) {
    const label = filters.min !== undefined && filters.max !== undefined
      ? `£${filters.min} – £${filters.max}`
      : filters.min !== undefined ? `£${filters.min}+` : `Under £${filters.max}`;
    chips.push({ label, onRemove: () => update({ min: undefined, max: undefined }) });
  }
  if (filters.eco) chips.push({ label: 'Organic or recycled', onRemove: () => update({ eco: false }) });
  if (filters.q) chips.push({ label: `“${filters.q}”`, onRemove: () => b.submitSearch('', true) });
  return chips;
};

interface BrowseToolbarProps {
  browse: ClothingBrowse;
  onOpenFilters: () => void;
  /** Phones: open the filter drawer and focus its Smart Search box. */
  onOpenSmartSearch?: () => void;
}

const BrowseToolbar: React.FC<BrowseToolbarProps> = ({ browse, onOpenFilters, onOpenSmartSearch }) => {
  const { page, loading, filters, update, clearAll, activeCount } = browse;
  const chips = chipsFor(browse);
  const total = page?.total ?? 0;
  const from = total === 0 ? 0 : (filters.page - 1) * 24 + 1;
  const to = Math.min(total, filters.page * 24);

  return (
    <div className="mb-2 sm:mb-5">
      <div className="flex items-center gap-2 sm:gap-3 sm:flex-wrap">
        <button
          type="button"
          onClick={onOpenFilters}
          className="lg:hidden shrink-0 inline-flex items-center gap-1.5 sm:gap-2 h-10 px-3 sm:px-4 bg-black text-white font-poppins text-[12px] font-semibold tracking-[0.06em] sm:tracking-[0.1em] uppercase"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters{activeCount ? ` (${activeCount})` : ''}
        </button>

        {onOpenSmartSearch && (
          <button
            type="button"
            onClick={onOpenSmartSearch}
            className="lg:hidden shrink-0 inline-flex items-center gap-1.5 h-10 px-3 sm:px-4 border border-[#78BE20] text-black font-poppins text-[12px] font-semibold tracking-[0.06em] sm:tracking-[0.1em] uppercase hover:bg-[#78BE20]/10 transition-colors"
          >
            <Sparkles className="w-4 h-4" style={{ color: GREEN }} aria-hidden />
            <span aria-hidden className="sm:hidden">Ask</span>
            <span className="sr-only sm:not-sr-only">Smart Search</span>
          </button>
        )}

        <p className="hidden sm:block font-poppins text-[13px] text-black/60" aria-live="polite">
          {loading && !page ? 'Loading…' : total === 0 ? 'No matches' : (
            <>
              Showing <span className="text-black font-semibold tabular-nums">{from}–{to}</span> of{' '}
              <span className="text-black font-semibold tabular-nums">{total.toLocaleString('en-GB')}</span> styles
            </>
          )}
        </p>

        <label className="ml-auto min-w-0 flex items-center gap-2 font-poppins text-[13px] text-black/60">
          <span className="hidden sm:inline">Sort</span>
          <select
            value={filters.sort}
            onChange={(e) => update({ sort: e.target.value as SortKey })}
            className="h-10 min-w-0 pl-3 pr-8 rounded-full border border-black/15 bg-white text-black text-[13px] font-medium outline-none focus:border-[#78BE20] appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22black%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-no-repeat bg-[right_0.75rem_center]"
          >
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </div>

      {chips.length > 0 && (
        <ul className="flex flex-nowrap sm:flex-wrap items-center gap-2 mt-1.5 sm:mt-4 -mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto sm:overflow-visible scrollbar-none">
          {chips.map((c) => (
            <li key={c.label} className="shrink-0">
              <button
                type="button"
                onClick={c.onRemove}
                className="inline-flex items-center gap-1.5 h-8 pl-3 pr-2 whitespace-nowrap rounded-full bg-black text-white font-poppins text-[12px] font-medium hover:bg-[#78BE20] transition-colors"
              >
                {c.label}
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
          {activeCount > 0 && (
            <li className="shrink-0">
              <button type="button" onClick={clearAll} className="h-8 px-2 whitespace-nowrap font-poppins text-[12px] font-semibold hover:underline" style={{ color: GREEN }}>
                Clear all
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default BrowseToolbar;
